import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { PTForm, PTTextInput } from "../../../common/components/ui";
import ForgotPasswordValidationSchemas from "../others/ForgotPasswordValidationSchemas";
import { ForgotPasswordInitialValues } from "../others/ForgotPasswordInitialValues";
import { Box, Link, Alert } from "@mui/material";
import { useAuth } from "../../../common/provider";
import PTTranslate from "../../../common/components/ui/PTTranslate";
import { ErrorMessage } from "formik";

const ForgotPasswordPage = ({ onBackToLogin }) => {
    const { forgotPassword, verifyResetCode, resetPassword, error } = useAuth();

    const [email, setEmail] = useState("");
    const [step, setStep] = useState(0);
    const { t } = useTranslation();
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);


    const initialValues = ForgotPasswordInitialValues[step] || {};
    const validationSchema = ForgotPasswordValidationSchemas[step] || {};

    const handleStepSubmit = async (values) => {
        setErrorMessage(null);
        setSuccessMessage(null);

        try {
            switch (step) {
                case 0:
                    setEmail(values.email);
                    await forgotPassword(values.email);
                    setStep(1);
                    break;
                case 1:
                    await verifyResetCode(email, values.code);
                    setStep(2);
                    break;
                case 2:
                    if (values.newPassword !== values.confirmPassword) {
                        setErrorMessage(t('login.errors.passwordReset.passwordMatchError'));
                        return;
                    }
                    await resetPassword(values.newPassword, values.confirmPassword)
                    setSuccessMessage(t('login.errors.passwordReset.passwordChangeSuccess'));
                    setTimeout(() => {
                        onBackToLogin();
                    }, (2000));

                    break;
                default:
                    break;
            }
        } catch (error) {

            if (error.response && error.response.data && error.response.data.message) {
                setErrorMessage(error.response.data.message);

            } else {
                setErrorMessage(t("login.errors.general"));
            }
        }
    };


    const inputStyles = {
        backgroundColor: "white !important",
        color: "black !important",
        "& .MuiOutlinedInput-root": {
            backgroundColor: "white !important",
            "&.Mui-focused": {
                backgroundColor: "white !important"
            },
            "&:hover": {
                backgroundColor: "white !important"
            }
        },
        "& .MuiInputBase-input": {
            backgroundColor: "white !important",
            padding: "8px 14px"
        }
    };

    const renderStepInputs = () => {

        switch (step) {
            case 0:
                return [
                    <PTTextInput
                        key="email"
                        hiddenLabel
                        name="email"
                        md={12}
                        lg={12}
                        placeHolder="login.email"
                        sx={inputStyles}
                        InputProps={{
                            style: { backgroundColor: "white" }
                        }}
                        inputProps={{
                            style: { backgroundColor: "white", color: "black" }
                        }}
                    />
                ];
            case 1:
                return [
                    <PTTextInput
                        key="code"
                        hiddenLabel
                        name="code"
                        md={12}
                        lg={12}
                        placeHolder="login.code"
                        sx={inputStyles}
                        InputProps={{
                            style: { backgroundColor: "white" }
                        }}
                        inputProps={{
                            style: { backgroundColor: "white", color: "black" }
                        }}
                    />
                ];
            case 2:
                return [
                    <PTTextInput
                        key="newPassword"
                        hiddenLabel
                        name="newPassword"
                        type="password"
                        md={12}
                        lg={12}
                        placeHolder="login.new_pwd"
                        sx={inputStyles}
                        InputProps={{
                            style: { backgroundColor: "white" }
                        }}
                        inputProps={{
                            style: { backgroundColor: "white", color: "black" }
                        }}
                    />,
                    <PTTextInput
                        key="confirmPassword"
                        hiddenLabel
                        name="confirmPassword"
                        type="password"
                        md={12}
                        lg={12}
                        placeHolder="login.confirm_pwd"
                        sx={inputStyles}
                        InputProps={{
                            style: { backgroundColor: "white" }
                        }}
                        inputProps={{
                            style: { backgroundColor: "white", color: "black" }
                        }}
                    />
                ];
            default:
                return null;
        }
    };

    const getPageDetails = () => {
        return {
            title: t(`login.errors.passwordReset.steps.${step}.title`),
            submitText: t(`login.errors.passwordReset.steps.${step}.submitText`)
        };
    };


    const { title, submitText } = getPageDetails();

    return (
        <>
            <Box sx={{
                textAlign: "center",
                fontWeight: 'bold',
                fontSize: '1.5rem',
                mb: 3
            }}>
                <PTTranslate tk={title} />
            </Box>

            <PTForm
                name="forgotPasswordForm"
                onSubmit={handleStepSubmit}
                initialValues={initialValues}
                validationSchema={validationSchema}
                submitButtonText={<PTTranslate tk={submitText} />}
                retableOn={false}
                fullWidthSubmitButton
                buttonStyle={{
                    width: "100%",
                    maxWidth: "100%",
                    backgroundColor: "#407BFF",
                    py: 1.1,
                    border: "none",
                    fontSize: "17px",
                    "&:hover": { backgroundColor: "#407BFF" },
                    "&:focus": { backgroundColor: "#407BFF" },
                }}
            >
                {renderStepInputs()}
            </PTForm>
            {errorMessage && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {errorMessage}
                </Alert>
            )}

            {successMessage && (
                <Alert severity="success" sx={{ mt: 2 }}>
                    <PTTranslate tk={successMessage} />
                </Alert>
            )}

            <Link
                component="button"
                onClick={onBackToLogin}
                underline="none"
                sx={{
                    fontSize: "0.875rem",
                    textAlign: "center",
                    display: "block",
                    mt: 2,
                    color: "407BFF"
                }}
            >
                <PTTranslate tk="login.backToLogin" />
            </Link>
        </>
    );
};

export default ForgotPasswordPage;